// Pure helpers for claim records. Amounts are integer cents. Dates are YYYY-MM-DD strings.

export const CLAIM_STATUSES = ['paid', 'pending', 'denied', 'partial_paid', 'appealed'];
export const STATUS_LABELS = { paid: 'Paid', pending: 'Pending', denied: 'Denied', partial_paid: 'Partial Paid', appealed: 'Appealed' };
export const UNITS_PER_HOUR = 4; // 15 minute billing units

const DAY = 86400000;

export function toISODate(value) {
  if (value == null || value === '') return '';
  if (value instanceof Date) return isNaN(value) ? '' : value.toISOString().slice(0, 10);
  if (typeof value === 'number') {
    // Excel serial date
    if (value > 20000 && value < 80000) {
      const d = new Date(Math.round((value - 25569) * DAY));
      return d.toISOString().slice(0, 10);
    }
    return '';
  }
  const s = String(value).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (m) {
    const y = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${y}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  }
  const d = new Date(s);
  return isNaN(d) ? '' : d.toISOString().slice(0, 10);
}

export function parseCents(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Math.round(value * 100);
  let s = String(value).trim();
  const neg = /^\(.*\)$/.test(s) || s.startsWith('-');
  s = s.replace(/[^0-9.]/g, '');
  if (!s) return 0;
  const n = Math.round(parseFloat(s) * 100);
  return neg ? -n : n;
}

export function parseUnits(value) {
  if (value == null || value === '') return 0;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function normalizeStatus(value) {
  const s = String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, ' ');
  if (!s) return 'pending';
  if (s.startsWith('partial')) return 'partial_paid';
  if (s.startsWith('paid')) return 'paid';
  if (s.startsWith('den')) return 'denied';
  if (s.startsWith('appeal')) return 'appealed';
  if (s.startsWith('pend') || s.startsWith('submit') || s.startsWith('open')) return 'pending';
  return 'pending';
}

export function daysBetween(a, b) {
  if (!a || !b) return null;
  const da = new Date(`${a}T00:00:00Z`).getTime();
  const db = new Date(`${b}T00:00:00Z`).getTime();
  if (isNaN(da) || isNaN(db)) return null;
  return Math.round((db - da) / DAY);
}

export function monthKey(iso) {
  return iso ? String(iso).slice(0, 7) : '';
}

export function monthLabel(key) {
  if (!key) return '';
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

// Derived fields computed the same way the Excel workbook does.
export function deriveClaim(c) {
  const unitsPaid = parseUnits(c.unitsPaid);
  const billed = Number(c.amountBilledCents) || 0;
  const paid = Number(c.amountPaidCents) || 0;
  return {
    ...c,
    unitsBilled: parseUnits(c.unitsBilled),
    unitsPaid,
    amountBilledCents: billed,
    amountPaidCents: paid,
    hoursPaid: unitsPaid / UNITS_PER_HOUR,
    adjustmentCents: billed - paid,
    serviceMonth: monthKey(c.dosStart),
    paidMonth: monthKey(c.datePaid),
    lagDays: c.datePaid ? daysBetween(c.dosEnd || c.dosStart, c.datePaid) : null,
    isOutstanding: c.status !== 'paid' && billed - paid > 0,
  };
}

export function normalizeClaimNumber(v) {
  return String(v || '').trim().toUpperCase().replace(/\s+/g, '');
}

function hashString(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0).toString(36);
}

// Stable identity so re-imports update instead of duplicating.
export function claimIdentity(c) {
  const cn = normalizeClaimNumber(c.claimNumber);
  if (cn) return `CN-${cn}`;
  return `HX-${hashString([c.payerName, c.patientName, c.dosStart, c.dosEnd, c.amountBilledCents].map(x => String(x || '').toLowerCase().trim()).join('|'))}`;
}

export function lastNMonths(n, from = new Date()) {
  const out = [];
  const d = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), 1));
  for (let i = n - 1; i >= 0; i--) {
    const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1));
    out.push(x.toISOString().slice(0, 7));
  }
  return out;
}

export function summarize(claims, { months = 12 } = {}) {
  const keys = lastNMonths(months);
  const byMonth = Object.fromEntries(keys.map(k => [k, { billed: 0, paid: 0, outstanding: 0, count: 0 }]));
  const byPayer = {};
  const byStatus = Object.fromEntries(CLAIM_STATUSES.map(s => [s, { count: 0, billed: 0, paid: 0 }]));
  let totalBilled = 0, totalPaid = 0;
  for (const c of claims) {
    totalBilled += c.amountBilledCents;
    totalPaid += c.amountPaidCents;
    if (byMonth[c.serviceMonth]) {
      byMonth[c.serviceMonth].billed += c.amountBilledCents;
      byMonth[c.serviceMonth].paid += c.amountPaidCents;
      byMonth[c.serviceMonth].outstanding += c.adjustmentCents;
      byMonth[c.serviceMonth].count += 1;
    }
    const pk = c.payerName || 'Unknown payer';
    byPayer[pk] = byPayer[pk] || { billed: 0, paid: 0, count: 0 };
    byPayer[pk].billed += c.amountBilledCents;
    byPayer[pk].paid += c.amountPaidCents;
    byPayer[pk].count += 1;
    const st = byStatus[c.status] || (byStatus[c.status] = { count: 0, billed: 0, paid: 0 });
    st.count += 1; st.billed += c.amountBilledCents; st.paid += c.amountPaidCents;
  }
  const paidLags = claims.filter(c => c.lagDays != null).map(c => c.lagDays);
  return {
    totalBilled, totalPaid,
    outstanding: totalBilled - totalPaid,
    claimCount: claims.length,
    paidCount: byStatus.paid.count,
    pendingCount: byStatus.pending.count,
    deniedCount: byStatus.denied.count,
    appealedCount: byStatus.appealed.count,
    partialCount: byStatus.partial_paid.count,
    openCount: claims.filter(c => c.isOutstanding).length,
    avgLagDays: paidLags.length ? Math.round(paidLags.reduce((a, b) => a + b, 0) / paidLags.length) : null,
    months: keys,
    byMonth, byPayer, byStatus,
  };
}
